'use client'

import { useMemo, useState } from 'react'
import { Copy, ExternalLink } from 'lucide-react'

import { Button } from '@/ui/atoms/button/Button'
import { Typography } from '@/ui/atoms/typography/Typography'

interface CardReferralShareProps {
  referralCode?: string
}

export function CardReferralShare({ referralCode }: CardReferralShareProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')

  const shareUrl = useMemo(() => {
    if (!referralCode || typeof window === 'undefined') {
      return ''
    }

    const url = new URL(window.location.href)
    url.searchParams.set('ref', referralCode)
    return `${window.location.origin}${url.pathname}${url.search}`
  }, [referralCode])

  const handleCopy = async () => {
    if (!shareUrl) {
      return
    }

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopyState('copied')
    } catch {
      setCopyState('error')
    }
  }

  const handleShareOnX = () => {
    if (!shareUrl) {
      return
    }

    const tweetText = 'I just joined the HypurrFi Card waitlist. Skip the line with my referral code.'
    const tweetUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}`
    window.open(tweetUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border/60 bg-background/30 p-4">
      <Typography variant="prompt" className="text-muted-foreground">
        Your referral code
      </Typography>

      <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
        <Typography variant="h4">{referralCode || '--'}</Typography>
        <Button size="sm" variant="outline" disabled={!shareUrl} onClick={handleCopy} prefixIcon={<Copy className="h-4 w-4" />}>
          {copyState === 'copied' ? 'Copied' : 'Copy link'}
        </Button>
      </div>

      <Button variant="outline" onClick={handleShareOnX} disabled={!shareUrl} prefixIcon={<ExternalLink className="h-4 w-4" />}>
        Share on X
      </Button>

      {copyState === 'error' && (
        <Typography variant="p" className="text-red-400 text-xs">
          Unable to copy link right now. Try again.
        </Typography>
      )}
    </div>
  )
}
